import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import filter from 'lodash/filter';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';

import { CreateInvoiceModalHkgComponent } from './../../create-invoice-modal-hkg/create-invoice-modal-hkg.component';
import { AuthService } from '../../../auth.service';
import { UserProfileService } from '../../../api/user-profile.service';

import { CountryCode } from '../../../models/countries-enum';
import { CustomerService } from '../../../api/customer.service';


@UntilDestroy()
@Component({
  selector: 'iv-status-types',
  templateUrl: './invoice-status.component.html',
  styleUrls: ['./invoice-status.component.scss']
})
export class InvoiceStatusTypesComponent implements OnInit {
  @Input() transactionTypes;
  @Input() parentStatus;
  @Input() userRoles;
  @Input() fromLanding = 'main';
  @Output() emitCreateCustomer = new EventEmitter();
  @Output() emitUpdateCustomer = new EventEmitter();

  startDate;
  endDate;
  query;

  // DeepLinking Integration
  externalLink = false;
  opportunity_number = null;
  opportunity_name = null;
  sourceSystem = null;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
    private userProfileService: UserProfileService,
    private authService: AuthService,
    private customerService: CustomerService
  ) { }

  ngOnInit() {
    if (this.activatedRoute.snapshot.queryParams) {
      this.startDate = this.activatedRoute.snapshot.queryParams.start_date;
      this.endDate = this.activatedRoute.snapshot.queryParams.end_date;
      this.query = this.activatedRoute.snapshot.queryParams.q;
      this.deepLinkingIntegration();
    }
  }

  deepLinkingIntegration () {
    if (this.activatedRoute.snapshot.queryParams['opportunity_number'] && 
      this.activatedRoute.snapshot.queryParams['opportunity_name'] &&
      this.activatedRoute.snapshot.queryParams['country'] &&
      this.authService.getPreviousURL().includes('deeplinking') &&
      this.userProfileService.getDeeplinkingCountryLocalStorage() === this.activatedRoute.snapshot.queryParams['country']) {
        this.externalLink = true;
        this.opportunity_number = this.activatedRoute.snapshot.queryParams['opportunity_number'];
        this.opportunity_name = this.activatedRoute.snapshot.queryParams['opportunity_name'];

        if (this.activatedRoute.snapshot.queryParams['country'] === CountryCode.JPN && 
          this.activatedRoute.snapshot.queryParams['source_system']) {
          this.sourceSystem = this.activatedRoute.snapshot.queryParams['source_system'];
        }
        
        this.showCreateInvoiceModal();
    } else {
      // Removing DeepLinking Info and Previous URL
      if (window.localStorage.getItem('deeplinking_user_locale')) {
        window.localStorage.removeItem('deeplinking_user_locale');
      }
      if (this.authService.getPreviousURL()) {
        window.localStorage.removeItem('previousURL');
      }
      if (this.userProfileService.getDeeplinkingCountryLocalStorage()) {
        window.localStorage.removeItem('deeplinking_country');
      }
    }
  }

  getIndex() {
    if (this.transactionTypes && this.parentStatus) {
      return this.transactionTypes.findIndex(transType => transType.id === this.parentStatus);
    }
    return 0;
  }

  navigateTopSection(status) {
    const queryParams: any = {};

    if (this.startDate && this.endDate) {
      queryParams.start_date = this.startDate;
      queryParams.end_date = this.endDate;
    }

    if (this.query) {
      queryParams.q = this.activatedRoute.snapshot.queryParams.q;
    }

    switch (this.fromLanding) {
      case 'main':
        this.router.navigate([`/landing/${status}`], {
          queryParams: queryParams
        });
        break;
      case 'customer':
        this.router.navigate([`/customer/${status}`], { queryParams: this.customerService.getCustomerLandingQueryParams() });
        break;
      default:
        break;
    }
  }

  // should be considered temporary until we can implement mat-tab-nav-bar for proper page linking
  selectedTabChange(event) {
    const status = this.transactionTypes[event.index].id;
    this.navigateTopSection(status);
  }

  showCreateInvoiceModal() {
    const params = {
      id: 'invoicemodal',
      minHeight: '535px',
      maxHeight: '735px',
      width: '520px',
      disableClose: false,
      data: {
        onCloseTrigger: false,
        opportunity: {
          externalLink: this.externalLink,
          number: this.opportunity_number,
          name: this.opportunity_name,
          sourceSystem: this.sourceSystem,
        }
      },
    };
    const dialogRef = this.dialog.open(CreateInvoiceModalHkgComponent, params);

    dialogRef.backdropClick()
      .pipe(untilDestroyed(this))
      .subscribe((e) => {
        params.data.onCloseTrigger = true;
        dialogRef.close(params.data);
      });

    dialogRef.afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe(
        (result) => {
          if (result?.opportunity?.externalLink) {
            let urlString = this.authService.getLastUrl();
            if (urlString && urlString.includes('?')) {
              let urlStringElements = [
                urlString.split('?')[0],
                urlString.split('?')[1]
                  .split('&')
                  .filter((item) => !item.includes('opportunity') && !item.includes('country'))
                  .join("&")
              ].filter((item) => item !== '');
              let newLastUrl = urlStringElements.length > 1 ? urlStringElements.join("?") : urlStringElements.toString();
              this.authService.setLastUrl(newLastUrl);
            }
            result.opportunity.externalLink = false;
            if (window.localStorage.getItem('deeplinking_user_locale')) {
              window.localStorage.removeItem('deeplinking_user_locale');
            }
            if (this.authService.getPreviousURL()) {
              window.localStorage.removeItem('previousURL');
            }
            if (this.userProfileService.getDeeplinkingCountryLocalStorage()) {
              window.localStorage.removeItem('deeplinking_country');
            }
            if (result.onCloseTrigger) {
              result.onCloseTrigger = false;
              this.router.navigate(['/landing']);
            }
          }
        }
      );
  }

  disableNewInvoice() {
    let disabled = false;
    const supportUserRole = this.getUser('support_user');
    // const financeUserRole = this.getUser('financial_user');
    if (supportUserRole && supportUserRole === 'true') {
      disabled = true;
    }
    return disabled;
  }

  getUser(roleName: string) {
    const userRole = filter(this.userRoles, function (user) { return (user.key === roleName); });
    if (userRole && userRole[0] && userRole.length > 0) {
      return userRole[0].value;
    }
    return null;
  }

  createNewCustomer() {
    this.emitCreateCustomer.emit();
  }

  updateCustomer() {
    this.emitUpdateCustomer.emit();
  }
}
